import type {MfkMealLinkV1} from '../../../contracts/meal-link-v1';
// Structural inputs only. These views consume canonical projected facts; they do
// not import the projector, its IO graph, or create another pricing policy.
export interface SyncedOptionSet{readonly name:string;readonly options:readonly {readonly name:string;readonly priceAdjustmentMinor:number}[]}
export interface SyncedOrderingProduct{readonly id:string;readonly name:string;readonly priceMinor:number;readonly optionSets:readonly SyncedOptionSet[]}
export interface SyncedComboPool{readonly id:string;readonly kind:string;readonly groups:readonly {readonly subPools:readonly {readonly choices:readonly {readonly type:string;readonly productId?:string}[]}[]}[]}
export interface SyncedRiceballDrinkPromotion{readonly active:boolean;readonly eligibleMainPoolIds:readonly string[];readonly drinks:readonly {readonly productId:string;readonly promoPriceMinor:number}[]}

export interface PairingProduct{
  readonly id:string;
  readonly name:string;
  readonly priceMinor:number;
  readonly optionSets?:readonly SyncedOptionSet[];
}

export interface PairingCartLine{
  readonly id:string;
  readonly productId:string;
  readonly name:string;
  readonly qty:number;
  readonly unitMinor:number;
  readonly serviceMode:'takeaway'|'dine-in';
  readonly detail?:string;
  readonly pairing?:unknown;
}

export const PAIRING_GROUP_PREFIX='套餐配對：' as const;
export const PAIRING_COMBO_PREFIX='套餐：' as const;
export const PAIRING_ROLE_PREFIX='角色：' as const;

function cleanParts(detail?:string){
  return String(detail??'').split(' · ').map(part=>part.trim()).filter(Boolean);
}

export function pairingGroupFromDetail(detail?:string){
  const part=cleanParts(detail).find(value=>value.startsWith(PAIRING_GROUP_PREFIX));
  return part?part.slice(PAIRING_GROUP_PREFIX.length).replace(/組$/,''):undefined;
}

export function pairingRoleFromDetail(detail?:string):'MAIN'|'SNACK'|undefined{
  const part=cleanParts(detail).find(value=>value.startsWith(PAIRING_ROLE_PREFIX));
  const role=part?.slice(PAIRING_ROLE_PREFIX.length);
  return role==='飯團'?'MAIN':role==='小食'?'SNACK':undefined;
}

export function isPairedComboLine(line:Pick<PairingCartLine,'detail'>){
  return Boolean(pairingGroupFromDetail(line.detail)&&pairingRoleFromDetail(line.detail));
}

export function stripPairingDetail(detail?:string){
  return cleanParts(detail)
    .filter(part=>!part.startsWith(PAIRING_GROUP_PREFIX)&&!part.startsWith(PAIRING_COMBO_PREFIX)&&!part.startsWith(PAIRING_ROLE_PREFIX))
    .join(' · ');
}

export function selectedOptionAdjustment(line:PairingCartLine,product:PairingProduct){
  const parts=cleanParts(stripPairingDetail(line.detail));
  let delta=0;
  for(const set of product.optionSets??[]){
    const prefix=set.name+'：';
    const segment=parts.find(part=>part.startsWith(prefix));
    if(!segment)continue;
    const names=segment.slice(prefix.length).split('、').map(value=>value.trim()).filter(Boolean);
    const selected=new Set(names);
    delta+=set.options.filter(option=>selected.has(option.name)).reduce((sum,option)=>sum+option.priceAdjustmentMinor,0);
  }
  return delta;
}

export function standalonePriceForLine(line:PairingCartLine,products:readonly PairingProduct[]){
  const product=products.find(row=>row.id===line.productId);
  return product?product.priceMinor+selectedOptionAdjustment(line,product):line.unitMinor;
}

export const RICEBALL_DRINK_PROMO_DETAIL='飯團優惠飲品' as const;

export interface PromoCartLine{
  readonly id:string;
  readonly productId:string;
  readonly name:string;
  readonly qty:number;
  readonly unitMinor:number;
  readonly serviceMode:'takeaway'|'dine-in';
  readonly detail?:string;
  readonly meal?:MfkMealLinkV1;
}

export interface PromoProductCatalogs{
  readonly takeaway:readonly SyncedOrderingProduct[];
  readonly 'dine-in':readonly SyncedOrderingProduct[];
}

function parts(detail?:string){
  return String(detail??'').split(' · ').map(part=>part.trim()).filter(Boolean);
}

export function stripRiceballDrinkPromotionDetail(detail?:string){
  return parts(detail).filter(part=>part!==RICEBALL_DRINK_PROMO_DETAIL).join(' · ');
}

function withPromoDetail(detail?:string){
  return [RICEBALL_DRINK_PROMO_DETAIL,stripRiceballDrinkPromotionDetail(detail)].filter(Boolean).join(' · ');
}

function eligibleMainProductIds(
  pools:readonly SyncedComboPool[],
  rule:SyncedRiceballDrinkPromotion,
){
  const ids=new Set<string>();
  const poolIds=new Set(rule.eligibleMainPoolIds);
  for(const pool of pools){
    if(pool.kind!=='MAIN_COURSE'||!poolIds.has(pool.id))continue;
    for(const group of pool.groups){
      for(const subPool of group.subPools){
        for(const choice of subPool.choices){
          if(choice.type==='PRODUCT'&&choice.productId)ids.add(choice.productId);
        }
      }
    }
  }
  return ids;
}

function lineProduct(line:PromoCartLine,catalogs:PromoProductCatalogs){
  return catalogs[line.serviceMode].find(product=>product.id===line.productId);
}

function standalonePrice(line:PromoCartLine,catalogs:PromoProductCatalogs){
  const product=lineProduct(line,catalogs);
  if(!product)return line.unitMinor;
  const cleanDetail=stripRiceballDrinkPromotionDetail(line.detail);
  return standalonePriceForLine({...line,detail:cleanDetail},catalogs[line.serviceMode]);
}

function normalizedUnitId(line:PromoCartLine,index:number){
  if(index===0)return line.id;
  return line.id+'::riceball-drink-promo:'+String(index+1);
}

export function applyRiceballDrinkPromotion<T extends PromoCartLine>(
  input:readonly T[],
  catalogs:PromoProductCatalogs,
  pools:readonly SyncedComboPool[],
  rule:SyncedRiceballDrinkPromotion|null,
):T[]{
  if(!rule?.active)return input.map(line=>{
    if(!parts(line.detail).includes(RICEBALL_DRINK_PROMO_DETAIL))return line;
    const detail=stripRiceballDrinkPromotionDetail(line.detail);
    return {...line,unitMinor:standalonePrice(line,catalogs),detail:detail||undefined} as T;
  });

  const mainIds=eligibleMainProductIds(pools,rule);
  if(!mainIds.size)return [...input];

  const mainUnits:{takeaway:number;'dine-in':number}={takeaway:0,'dine-in':0};
  for(const line of input){
    if(isPairedComboLine(line)||line.meal)continue;
    if(!mainIds.has(line.productId))continue;
    mainUnits[line.serviceMode]+=Math.max(0,Math.floor(line.qty));
  }

  const promoByProduct=new Map(rule.drinks.map(drink=>[drink.productId,drink] as const));
  const remaining={...mainUnits};
  const output:T[]=[];

  for(const line of input){
    const promo=promoByProduct.get(line.productId);
    if(!promo||isPairedComboLine(line)||line.meal){
      output.push(line);
      continue;
    }
    const product=lineProduct(line,catalogs);
    const cleanDetail=stripRiceballDrinkPromotionDetail(line.detail);
    const normal=standalonePrice({...line,detail:cleanDetail},catalogs);
    const optionAdjustment=product?normal-product.priceMinor:0;
    const qty=Math.max(0,Math.floor(line.qty));

    for(let index=0;index<qty;index++){
      const eligible=remaining[line.serviceMode]>0;
      if(eligible)remaining[line.serviceMode]-=1;
      const unitMinor=eligible?promo.promoPriceMinor+optionAdjustment:normal;
      output.push({
        ...line,
        id:normalizedUnitId(line,index),
        qty:1,
        unitMinor,
        detail:(eligible?withPromoDetail(cleanDetail):cleanDetail)||undefined,
      } as T);
    }
  }
  return output;
}

export function riceballDrinkPromotionStateEqual(
  left:readonly PromoCartLine[],
  right:readonly PromoCartLine[],
){
  if(left.length!==right.length)return false;
  for(let index=0;index<left.length;index++){
    const a=left[index]!,b=right[index]!;
    if(a.id!==b.id||a.productId!==b.productId||a.qty!==b.qty||a.unitMinor!==b.unitMinor||a.serviceMode!==b.serviceMode||(a.detail??'')!==(b.detail??''))return false;
  }
  return true;
}
